import React, { useState } from 'react';
import CalendarConfig from '../../../../consts/CalendarConfig';
import styles from './PatientHistoryItem.module.css';
import parts from '../moduleCard/moduleParts.module.css';
import DateChip from '../moduleCard/DateChip';
import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined';
import ExpandLessOutlinedIcon from '@mui/icons-material/ExpandLessOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import { Chip, Button, IconButton, Tooltip } from '@mui/material';
import ColorPalette from '../../../../colorpalette/ColorPalette';

/**
 * Um registro de queixa/evolução: linha de data e autor, depois os dois textos
 * empilhados. Registros anulados nascem recolhidos (só a linha de chips) e
 * abrem ao clique.
 */
const PatientHistoryItem = ({
    id,
    date,
    assessment,
    evolution,
    isAnnulled = false,
    annulledBy = null,
    annulledAt = null,
    annulReason = null,
    replacesRecordId = null,
    replacementRecordId = null,
    onAnnul = null,
    onCreateReplacement = null,
    createdBy = null
}) => {
    const formattedDate = CalendarConfig.GetDDMMAAAA(date);
    const formattedAnnulledDate = annulledAt ? CalendarConfig.GetDDMMAAAA(annulledAt) : null;
    const [expanded, setExpanded] = useState(!isAnnulled);

    const toggleExpanded = () => {
        setExpanded(!expanded);
    };

    const handleAnnul = (e) => {
        e.stopPropagation();
        if (onAnnul && id) {
            onAnnul(id);
        }
    };

    const handleCreateReplacement = (e) => {
        e.stopPropagation();
        if (onCreateReplacement && id) {
            onCreateReplacement(id, assessment, evolution);
        }
    };

    const collapsed = isAnnulled && !expanded;
    const canAnnul = !isAnnulled && !replacementRecordId && onAnnul;

    const renderText = (text) => {
        const value = (text || '').replace(/\\n/g, '\n').trim();
        if (!value) return <span className={`${parts.itemText} ${styles.emptyText}`}>Não informado</span>;
        return <span className={parts.itemText}>{value}</span>;
    };

    return (
        <div
            className={`${parts.item} ${styles.record} ${isAnnulled ? styles.annulledRecord : ''} ${collapsed ? styles.collapsed : ''}`}
            onClick={isAnnulled ? toggleExpanded : undefined}
        >
            <div className={parts.itemRow}>
                <div className={parts.chips}>
                    <DateChip label={formattedDate} compact />

                    {isAnnulled && (
                        <Chip
                            icon={<CancelOutlinedIcon fontSize="small" />}
                            label="Anulado"
                            size="small"
                            sx={{
                                backgroundColor: ColorPalette.errorLight,
                                color: ColorPalette.error,
                                fontWeight: 600,
                                fontSize: '12px',
                                height: '24px',
                                '& .MuiChip-icon': { color: ColorPalette.error, fontSize: '14px' }
                            }}
                        />
                    )}

                    {replacesRecordId && (
                        <span className={`${parts.chip} ${parts.chipAccent}`}>Substitui um registro anulado</span>
                    )}
                </div>

                <div className={styles.rowEnd}>
                    {createdBy && (
                        <Tooltip title="Registrado por">
                            <span className={parts.itemMeta}>
                                <PersonOutlinedIcon />
                                {createdBy}
                            </span>
                        </Tooltip>
                    )}

                    {canAnnul && (
                        <Button
                            variant="text"
                            size="small"
                            startIcon={<CancelOutlinedIcon />}
                            onClick={handleAnnul}
                            className={styles.annulButton}
                            sx={{
                                color: ColorPalette.textLight,
                                fontSize: '12px',
                                fontWeight: 500,
                                textTransform: 'none',
                                minWidth: 'auto',
                                padding: '2px 8px',
                                '&:hover': { color: ColorPalette.error, backgroundColor: ColorPalette.errorLight }
                            }}
                        >
                            Anular
                        </Button>
                    )}

                    {isAnnulled && (
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                toggleExpanded();
                            }}
                            sx={{ color: ColorPalette.textLight }}
                        >
                            {expanded ? <ExpandLessOutlinedIcon fontSize="small" /> : <ExpandMoreOutlinedIcon fontSize="small" />}
                        </IconButton>
                    )}
                </div>
            </div>

            {isAnnulled && expanded && (
                <div className={styles.annulmentInfo}>
                    <span className={styles.annulmentText}>
                        Anulado em {formattedAnnulledDate} {annulledBy && `por ${annulledBy}`}
                    </span>

                    {annulReason && (
                        <span className={styles.annulmentReason}>Motivo: {annulReason}</span>
                    )}

                    {!replacementRecordId && onCreateReplacement && (
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<EditOutlinedIcon />}
                            onClick={handleCreateReplacement}
                            sx={{ alignSelf: 'flex-start', fontSize: '12px', fontWeight: 600, textTransform: 'none', marginTop: '4px' }}
                        >
                            Criar substituição
                        </Button>
                    )}
                </div>
            )}

            {replacementRecordId && !isAnnulled && (
                <span className={parts.itemMeta}>
                    <HistoryOutlinedIcon />
                    Este registro foi substituído por um novo registro
                </span>
            )}

            {(!isAnnulled || expanded) && (
                <div className={styles.body}>
                    <div className={styles.block}>
                        <span className={parts.itemTitle}>Queixa</span>
                        {renderText(assessment)}
                    </div>

                    <div className={styles.block}>
                        <span className={parts.itemTitle}>Evolução</span>
                        {renderText(evolution)}
                    </div>
                </div>
            )}
        </div>
    );
};

export default PatientHistoryItem;
